import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { dashboardAPI, lotesAPI } from '../lib/api';
import { formatCurrency, formatNumber, getCategoryColor, CATEGORIAS } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Bird,
  CurrencyDollar,
  ChartLine,
  Warning,
  ArrowRight,
  Plus,
  Skull,
  TrendUp,
  Funnel,
  Gauge,
  Scales,
  ChartBar,
} from '@phosphor-icons/react';
import {
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  Legend,
  Cell,
  ReferenceLine,
  PieChart,
  Pie,
} from 'recharts';

// ==================== COMPONENTE GAUGE (Velocímetro) ====================
const GaugeChart = ({ value, min = 0, max = 3, estado, estandar }) => {
  // Calcular ángulo para el valor (180° arco)
  const normalizedValue = Math.min(Math.max(value || 0, min), max);
  const percentage = ((normalizedValue - min) / (max - min)) * 100;
  const angle = (percentage / 100) * 180;
  
  // Colores según estado
  const getColor = () => {
    switch (estado) {
      case 'excelente': return '#10B981'; // Verde esmeralda
      case 'bueno': return '#F59E0B'; // Amarillo
      case 'regular': return '#F97316'; // Naranja
      case 'malo': return '#EF4444'; // Rojo
      default: return '#9CA3AF'; // Gris
    }
  };

  const getLabel = () => {
    switch (estado) {
      case 'excelente': return 'Excelente';
      case 'bueno': return 'Bueno';
      case 'regular': return 'Regular';
      case 'malo': return 'Deficiente';
      default: return 'Sin datos';
    }
  };

  // Datos para el arco del gauge
  const gaugeData = [
    { name: 'Excelente', value: 33.33, fill: '#10B981' },
    { name: 'Bueno', value: 16.67, fill: '#F59E0B' },
    { name: 'Regular', value: 16.67, fill: '#F97316' },
    { name: 'Deficiente', value: 33.33, fill: '#EF4444' },
  ];

  return (
    <div className="relative w-full flex flex-col items-center">
      <div className="relative w-64 h-36">
        {/* Arco de fondo */}
        <svg viewBox="0 0 200 110" className="w-full h-full">
          {/* Arco de fondo gris */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="16"
            strokeLinecap="round"
          />
          {/* Zonas de color */}
          <path
            d="M 20 100 A 80 80 0 0 1 60 35"
            fill="none"
            stroke="#10B981"
            strokeWidth="16"
            strokeLinecap="round"
          />
          <path
            d="M 60 35 A 80 80 0 0 1 100 20"
            fill="none"
            stroke="#F59E0B"
            strokeWidth="16"
          />
          <path
            d="M 100 20 A 80 80 0 0 1 140 35"
            fill="none"
            stroke="#F97316"
            strokeWidth="16"
          />
          <path
            d="M 140 35 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="#EF4444"
            strokeWidth="16"
            strokeLinecap="round"
          />
          
          {/* Aguja */}
          {value && (
            <g transform={`rotate(${angle - 90}, 100, 100)`}>
              <line
                x1="100"
                y1="100"
                x2="100"
                y2="30"
                stroke={getColor()}
                strokeWidth="4"
                strokeLinecap="round"
              />
              <circle cx="100" cy="100" r="8" fill={getColor()} />
            </g>
          )}
          
          {/* Centro */}
          <circle cx="100" cy="100" r="5" fill="#374151" />
        </svg>
        
        {/* Etiquetas de rango */}
        <div className="absolute bottom-0 left-2 text-xs text-gray-500">{min}</div>
        <div className="absolute bottom-0 right-2 text-xs text-gray-500">{max}</div>
      </div>
      
      {/* Valor actual */}
      <div className="text-center mt-2">
        <div className="text-3xl font-bold" style={{ color: getColor() }}>
          {value ? value.toFixed(2) : '-'}
        </div>
        <div className="text-sm text-muted-foreground">
          ICA Actual
        </div>
        <Badge 
          className="mt-1"
          style={{ 
            backgroundColor: getColor() + '20', 
            color: getColor(),
            border: `1px solid ${getColor()}`
          }}
        >
          {getLabel()}
        </Badge>
        {estandar && (
          <div className="text-xs text-muted-foreground mt-1">
            Estándar Cobb-500: {estandar}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== TOOLTIP PERSONALIZADO ====================
const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-700 mb-1">{label}</p>
        {payload.map((entry) => (
          <p key={entry.dataKey || entry.name} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {formatter ? formatter(entry.value) : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [allLotes, setAllLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtro por lote
  const [selectedLoteId, setSelectedLoteId] = useState('todos');
  const [loteStats, setLoteStats] = useState(null);
  const [loadingLote, setLoadingLote] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedLoteId && selectedLoteId !== 'todos') {
      loadLoteStats(selectedLoteId);
    } else {
      setLoteStats(null);
    }
  }, [selectedLoteId]);

  const loadData = async () => {
    try {
      const [statsRes, lotesRes, allLotesRes] = await Promise.all([
        dashboardAPI.getStats(),
        lotesAPI.getAll('activo'),
        lotesAPI.getAll(),
      ]);
      setStats(statsRes.data);
      setLotes(lotesRes.data);
      setAllLotes(allLotesRes.data);
    } catch (error) {
      toast.error('Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadLoteStats = async (loteId) => {
    setLoadingLote(true);
    try {
      const response = await dashboardAPI.getLoteStats(loteId);
      setLoteStats(response.data);
    } catch (error) {
      toast.error('Error al cargar datos del lote');
    } finally {
      setLoadingLote(false);
    }
  };

  // ==================== PREPARACIÓN DE DATOS ====================
  
  // Datos para gráfica de crecimiento (Peso Real vs Cobb-500)
  const growthData = loteStats?.grafico_crecimiento?.filter(d => d.peso_real !== null) || [];
  
  // Datos para el gauge de ICA
  const icaData = loteStats?.grafico_ica || {};
  
  // Datos para gráfica de costos
  const costData = loteStats?.grafico_costos || [];

  // Datos de distribución de gastos para "todos" (vista general)
  const pieData = stats?.resumen_por_categoria
    ? Object.entries(stats.resumen_por_categoria).map(([key, value]) => ({
        name: CATEGORIAS.find((c) => c.value === key)?.label || key,
        value: value,
        color: getCategoryColor(key),
      }))
    : [];

  const barData = lotes.map((lote) => ({
    name: lote.nombre.length > 10 ? lote.nombre.substring(0, 10) + '...' : lote.nombre,
    inversion: lote.total_inversion,
    dias: lote.dias_transcurridos,
  }));

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-80" />
            <Skeleton className="h-80" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="dashboard">
        {/* Header with Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Dashboard
            </h1>
            <p className="text-muted-foreground">
              Resumen general de la operación avícola
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Filtro por Lote */}
            <div className="flex items-center gap-2">
              <Funnel size={18} className="text-muted-foreground" />
              <Select value={selectedLoteId} onValueChange={setSelectedLoteId}>
                <SelectTrigger className="w-[200px]" data-testid="filtro-lote">
                  <SelectValue placeholder="Filtrar por lote" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los lotes</SelectItem>
                  {allLotes.map((lote) => (
                    <SelectItem key={lote.id} value={lote.id}>
                      {lote.nombre} ({lote.estado})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Link to="/lotes/nuevo">
              <Button className="bg-[#E73425] hover:bg-[#D81C23] btn-animate" data-testid="new-lote-btn">
                <Plus size={18} className="mr-2" />
                Nuevo Lote
              </Button>
            </Link>
          </div>
        </div>

        {/* ==================== VISTA ESPECÍFICA DE LOTE ==================== */}
        {selectedLoteId !== 'todos' && loteStats && (
          <div className="space-y-6 p-4 bg-gradient-to-r from-[#E73425]/5 to-[#F69031]/5 rounded-xl border border-[#E73425]/20">
            {/* Header del Lote */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Bird size={24} className="text-[#E73425]" />
                  {loteStats.lote.nombre}
                </h2>
                <p className="text-sm text-muted-foreground">
                  Día {loteStats.lote.dias_transcurridos} | Estado: {loteStats.lote.estado}
                </p>
              </div>
              <Badge className={loteStats.lote.estado === 'activo' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                {loteStats.lote.estado}
              </Badge>
            </div>

            {/* Métricas Clave del Lote */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card className="bg-white/80">
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">Pollos Inicial</p>
                  <p className="text-lg font-bold">{formatNumber(loteStats.pollos.inicial)}</p>
                </CardContent>
              </Card>
              <Card className="bg-white/80">
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">Pollos Actual</p>
                  <p className="text-lg font-bold text-emerald-600">{formatNumber(loteStats.pollos.actual)}</p>
                </CardContent>
              </Card>
              <Card className="bg-white/80">
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">Mortalidad</p>
                  <p className="text-lg font-bold text-red-600">
                    {formatNumber(loteStats.pollos.mortalidad)}
                    <span className="text-xs font-normal ml-1">({loteStats.pollos.porcentaje_mortalidad}%)</span>
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white/80">
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">Inversión Total</p>
                  <p className="text-lg font-bold text-[#E73425]">
                    RD$ {formatNumber(loteStats.inversion.total_dop)}
                  </p>
                </CardContent>
              </Card>
              <Card className={`bg-white/80 ${loteStats.resultado.ganancia_neta_dop >= 0 ? 'border-emerald-300' : 'border-red-300'} border-2`}>
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">Ganancia Neta</p>
                  <p className={`text-lg font-bold ${loteStats.resultado.ganancia_neta_dop >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    RD$ {formatNumber(loteStats.resultado.ganancia_neta_dop)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* ==================== GRÁFICOS PRINCIPALES ==================== */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              
              {/* 1. GRÁFICA DE LÍNEAS: Peso Real vs Cobb-500 */}
              <Card className="bg-white/90 lg:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendUp size={18} className="text-[#E73425]" />
                    Curva de Crecimiento: Peso Real vs Cobb-500
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {growthData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={growthData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="semana_label" 
                          tick={{ fontSize: 11 }} 
                          axisLine={{ stroke: '#94a3b8' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 11 }} 
                          axisLine={{ stroke: '#94a3b8' }}
                          label={{ value: 'Peso (lbs)', angle: -90, position: 'insideLeft', fontSize: 11 }}
                        />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              const real = payload.find(p => p.dataKey === 'peso_real');
                              const cobb = payload.find(p => p.dataKey === 'peso_cobb_500');
                              const diff = real && cobb ? (real.value - cobb.value).toFixed(3) : null;
                              return (
                                <div className="bg-white p-3 border rounded-lg shadow-lg">
                                  <p className="font-semibold mb-1">{label}</p>
                                  {real && <p className="text-[#E73425] text-sm">Peso Real: {real.value} lbs</p>}
                                  {cobb && <p className="text-[#2196F3] text-sm">Cobb-500: {cobb.value} lbs</p>}
                                  {diff && (
                                    <p className={`text-sm font-medium ${parseFloat(diff) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                      Diferencia: {diff > 0 ? '+' : ''}{diff} lbs
                                    </p>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line
                          type="monotone"
                          dataKey="peso_cobb_500"
                          name="Estándar Cobb-500"
                          stroke="#2196F3"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          dot={{ fill: '#2196F3', r: 4 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="peso_real"
                          name="Peso Real"
                          stroke="#E73425"
                          strokeWidth={3}
                          dot={{ fill: '#E73425', r: 5 }}
                          activeDot={{ r: 7, fill: '#E73425' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      <div className="text-center">
                        <Scales size={48} className="mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No hay datos de peso registrados</p>
                        <Link to={`/lotes/${selectedLoteId}/record`} className="text-[#F69031] hover:underline text-xs">
                          Ir a Record de Granja →
                        </Link>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* 2. GAUGE: Índice de Conversión Alimenticia */}
              <Card className="bg-white/90">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Gauge size={18} className="text-[#F69031]" />
                    Índice de Conversión (ICA)
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center justify-center">
                  {icaData.actual ? (
                    <>
                      <GaugeChart 
                        value={icaData.actual} 
                        min={0.5} 
                        max={3.0} 
                        estado={icaData.estado}
                        estandar={icaData.estandar}
                      />
                      <div className="mt-4 text-center text-xs text-muted-foreground space-y-1">
                        <p>Semana actual: {icaData.semana_actual}</p>
                        <p>Alimento consumido: {formatNumber(icaData.alimento_consumido_lbs)} lbs</p>
                        <p>Pollos actuales: {formatNumber(icaData.pollos_actuales)}</p>
                      </div>
                    </>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      <div className="text-center">
                        <Gauge size={48} className="mx-auto mb-2 opacity-30" />
                        <p className="text-sm">Sin datos suficientes para calcular ICA</p>
                        <p className="text-xs mt-1">Registre peso y alimento en Record de Granja</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* 3. GRÁFICA DE BARRAS: Desglose de Costos */}
            <Card className="bg-white/90">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <ChartBar size={18} className="text-emerald-600" />
                  Desglose de Costos por Categoría (RD$)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {costData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={costData} 
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                      <XAxis 
                        type="number" 
                        tick={{ fontSize: 10 }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      />
                      <YAxis 
                        type="category" 
                        dataKey="nombre" 
                        tick={{ fontSize: 11 }}
                        width={75}
                      />
                      <Tooltip
                        formatter={(value) => [`RD$ ${formatNumber(value)}`, 'Monto']}
                        contentStyle={{
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                        }}
                      />
                      <Bar 
                        dataKey="monto" 
                        radius={[0, 4, 4, 0]}
                        maxBarSize={30}
                      >
                        {costData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <CurrencyDollar size={48} className="mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No hay gastos registrados</p>
                      <Link to={`/gastos/nuevo?lote=${selectedLoteId}`} className="text-[#F69031] hover:underline text-xs">
                        Registrar gasto →
                      </Link>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Resumen de Ventas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-white/80">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground mb-1">Total Ventas</p>
                  <p className="text-lg font-bold text-emerald-600">
                    RD$ {formatNumber(loteStats.ventas.total_dop)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {loteStats.ventas.total_facturas} facturas
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white/80">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground mb-1">Cobrado</p>
                  <p className="text-lg font-bold text-blue-600">
                    RD$ {formatNumber(loteStats.ventas.cobrado_dop)}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white/80">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground mb-1">Por Cobrar</p>
                  <p className="text-lg font-bold text-orange-600">
                    RD$ {formatNumber(loteStats.ventas.pendiente_dop)}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ==================== VISTA GENERAL (TODOS LOS LOTES) ==================== */}
        
        {/* Alertas - Solo cuando está en "todos" */}
        {selectedLoteId === 'todos' && stats?.alertas && stats.alertas.length > 0 && (
          <div className="space-y-2" data-testid="alerts-section">
            {stats.alertas.map((alerta, idx) => (
              <div
                key={idx}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  alerta.tipo === 'critico'
                    ? 'bg-red-50 border-red-200'
                    : alerta.tipo === 'advertencia'
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-blue-50 border-blue-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Warning
                    size={24}
                    weight="fill"
                    className={
                      alerta.tipo === 'critico'
                        ? 'text-red-500'
                        : alerta.tipo === 'advertencia'
                        ? 'text-orange-500'
                        : 'text-blue-500'
                    }
                  />
                  <div>
                    <p className="font-medium">{alerta.lote_nombre}</p>
                    <p className="text-sm text-muted-foreground">{alerta.mensaje}</p>
                  </div>
                </div>
                <Link to={`/lotes/${alerta.lote_id}`}>
                  <Button variant="ghost" size="sm">
                    Ver <ArrowRight size={16} className="ml-1" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* Metric Cards - Solo cuando está en "todos" */}
        {selectedLoteId === 'todos' && (
          <>
            <div className="dashboard-grid" data-testid="metrics-grid">
              <Card className="metric-card card-hover">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="metric-label">Lotes Activos</p>
                      <p className="metric-value text-[#E73425]">{stats?.lotes_activos || 0}</p>
                    </div>
                    <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                      <Bird size={24} className="text-[#E73425]" weight="fill" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="metric-card card-hover">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="metric-label">Pollos Activos</p>
                      <p className="metric-value text-[#F69031]">
                        {formatNumber(stats?.total_pollos_activos || 0)}
                      </p>
                    </div>
                    <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                      <Bird size={24} className="text-[#F69031]" weight="fill" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="metric-card card-hover">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="metric-label">Inversión Activa</p>
                      <p className="metric-value text-emerald-600">
                        {formatCurrency(stats?.inversion_total_activa || 0)}
                      </p>
                    </div>
                    <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CurrencyDollar size={24} className="text-emerald-600" weight="fill" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="metric-card card-hover">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="metric-label">Lotes Cerrados</p>
                      <p className="metric-value text-slate-600">{stats?.lotes_cerrados || 0}</p>
                    </div>
                    <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                      <ChartLine size={24} className="text-slate-600" weight="fill" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts para vista general */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pie Chart - Distribución de Gastos */}
              <Card className="border-border/60">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    Distribución de Gastos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      No hay datos de gastos
                    </div>
                  )}
                  {pieData.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4 justify-center">
                      {pieData.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 text-xs">
                          <div
                            className="w-3 h-3 rounded"
                            style={{ backgroundColor: item.color }}
                          />
                          <span>{item.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Bar Chart - Inversión por Lote */}
              <Card className="border-border/60">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    Inversión por Lote Activo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {barData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={barData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                          formatter={(value, name) =>
                            name === 'inversion'
                              ? formatCurrency(value)
                              : `${value} días`
                          }
                          contentStyle={{
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                          }}
                        />
                        <Bar dataKey="inversion" fill="#E73425" radius={[4, 4, 0, 0]} name="Inversión" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      No hay lotes activos
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Active Lots List */}
            {lotes.length > 0 && (
              <Card className="border-border/60">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    Lotes Activos
                  </CardTitle>
                  <Link to="/lotes">
                    <Button variant="ghost" size="sm">
                      Ver todos <ArrowRight size={16} className="ml-1" />
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {lotes.slice(0, 5).map((lote) => (
                      <div
                        key={lote.id}
                        onClick={() => setSelectedLoteId(lote.id)}
                        className="flex items-center justify-between p-4 rounded-lg border border-border/60 hover:border-primary/50 hover:bg-muted/30 transition-all cursor-pointer"
                        data-testid={`lote-card-${lote.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Bird size={20} className="text-[#E73425]" weight="fill" />
                          </div>
                          <div>
                            <p className="font-medium">{lote.nombre}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatNumber(lote.cantidad_pollitos)} pollitos
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold">{formatCurrency(lote.total_inversion)}</p>
                            <p className="text-sm text-muted-foreground">
                              Día {lote.dias_transcurridos}
                            </p>
                          </div>
                          <Badge
                            variant="outline"
                            className={
                              lote.dias_transcurridos >= 60
                                ? 'alert-critical'
                                : lote.dias_transcurridos >= 45
                                ? 'alert-warning'
                                : 'alert-success'
                            }
                          >
                            {lote.dias_transcurridos >= 60
                              ? 'Nuevo ciclo'
                              : lote.dias_transcurridos >= 45
                              ? 'Vender'
                              : 'Activo'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Empty state */}
        {stats?.lotes_activos === 0 && selectedLoteId === 'todos' && (
          <Card className="border-border/60">
            <CardContent className="py-12">
              <div className="empty-state">
                <Bird size={64} className="text-muted-foreground/50 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No hay lotes activos</h3>
                <p className="text-muted-foreground mb-6">
                  Comience creando su primer lote de pollos de engorde
                </p>
                <Link to="/lotes/nuevo">
                  <Button className="bg-[#E73425] hover:bg-[#D81C23]">
                    <Plus size={18} className="mr-2" />
                    Crear Primer Lote
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading state for lote stats */}
        {loadingLote && (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        )}
      </div>
    </Layout>
  );
}
